import { useState, useEffect, useCallback, useRef } from "react";
import { BrowserMultiFormatReader } from "@zxing/library";
import { createLogger } from "./frontendLogger";
import "./App.css";

function App() {
  const log = useRef(createLogger("MainApp")).current;
  const toggleRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [barcode, setBarcode] = useState("");
  const [manualSearch, setManualSearch] = useState("");
  const [isManualMode, setIsManualMode] = useState(false);
  const [good, setGood] = useState(null);
  const [stocks, setStocks] = useState([]);
  const [prices, setPrices] = useState([]);
  const [similarItems, setSimilarItems] = useState([]);

  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const codeReader = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalItems, setModalItems] = useState([]);

  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const apiKey = pathParts[0];

  const API_BASE =
    process.env.NODE_ENV === "production"
      ? "https://item-checker-api.dastyor-tech.uz"
      : "http://localhost:4000";

  const apiPost = useCallback(
    async (endpoint, body, meta = {}) => {
      const started = Date.now();
      const url = `${API_BASE}${endpoint}/${apiKey}`;
      log.info("api.request", {
        endpoint,
        msSinceNav: Math.round(performance.now()),
        bodyKeys: body && typeof body === "object" ? Object.keys(body) : [],
        ...meta,
      });

      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body ?? {}),
      });

      log.info("api.response", {
        endpoint,
        status: resp.status,
        ok: resp.ok,
        ms: Date.now() - started,
        ...meta,
      });

      return resp;
    },
    [API_BASE, apiKey, log]
  );

  const startScanner = async () => {
    setScanning(true);
    log.info("scanner.start");
    codeReader.current = new BrowserMultiFormatReader();

    try {
      const constraints = {
        video: { facingMode: { ideal: "environment" } },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute("playsinline", true);
        await videoRef.current.play();
      }

      codeReader.current.decodeFromVideoDevice(
        null,
        videoRef.current,
        (result, err) => {
          if (result) {
            const text = result.getText();
            log.info("scanner.scan", { len: String(text).length });
            stopScanner();
            handleSearch(text, true);
          }
          if (
            err &&
            !(err.name === "Камера не найдена" || err.name === "NotFoundError")
          ) {
            console.error(err);
          }
        }
      );
    } catch (err) {
      console.error("Camera error:", err);
      log.error("scanner.error", { error: err?.message || String(err) });
      alert("Камера не найдена " + err.message);
      setScanning(false);
    }
  };

  const stopScanner = useCallback(() => {
    log.info("scanner.stop");
    if (codeReader.current) {
      codeReader.current.reset();
    }
    if (videoRef.current?.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setScanning(false);
  }, [log]);

  const loadItemDetails = useCallback(
    async (item) => {
      const itemId = item.item.id;
      const itemName = item.item.name;
      const itemArticul = item.item.articul || null;

      log.info("item.load.start", {
        itemId,
        hasArticul: Boolean(itemArticul),
        nameLen: String(itemName || "").length,
      });

      setGood({
        id: itemId,
        name: item.item.name,
        articul: itemArticul,
        quantity: item.quantity.common,
        price: item.price,
        image: item.image_url || null,
        color: item.item.color?.name || null,
        size: item.item.size?.name || null,
      });

      const stockResponse = await apiPost("/api/quantity", { item_id: itemId });

      const stockData = await stockResponse.json();
      setStocks(stockData.ok ? stockData.result : []);

      const priceResponse = await apiPost("/api/prices", {
        item_ids: [itemId],
      });

      const priceData = await priceResponse.json();
      setPrices(priceData.ok ? priceData.result : []);

      const similarResponse = await apiPost("/api/search_similar", {
        name: itemName,
        articul: itemArticul,
        stock_id: 1,
        price_type_id: 1,
        image_size: 1,
      });

      const similarData = await similarResponse.json();

      if (similarData.length > 1) {
        const filtered = similarData?.filter((i) => {
          return (
            i.item.id !== itemId &&
            i.item.name === itemName &&
            i.item.articul === itemArticul
          );
        });
        setSimilarItems(filtered);
      } else {
        setSimilarItems([]);
      }

      log.info("item.load.done", {
        itemId,
        stocks: stockData?.ok ? stockData?.result?.length ?? 0 : 0,
        prices: priceData?.ok ? priceData?.result?.length ?? 0 : 0,
        similar: Array.isArray(similarData) ? similarData.length : 0,
      });
    },
    [apiPost, log]
  );

  const handleSearch = useCallback(
    async (searchTerm, fromScanner = false) => {
      if (!searchTerm.trim() || !apiKey) return;

      try {
        setNotFound(false);
        setLoading(true);

        log.info("search.start", {
          fromScanner,
          len: String(searchTerm).length,
          manual: isManualMode,
        });

        const response = await apiPost(
          "/api/search",
          {
            search: searchTerm,
            stock_id: 1,
            price_type_id: 1,
            image_size: 1,
          },
          { fromScanner }
        );

        const data = await response.json();

        log.info("search.result", {
          ok: Boolean(data?.ok),
          count: Array.isArray(data?.result) ? data.result.length : 0,
        });

        if (!(data.ok && data.result.length > 0)) {
          setNotFound(true);
          setGood(null);
          setStocks([]);
          setPrices([]);
          setSimilarItems([]);
          return;
        }

        if (isManualMode && !fromScanner) {
          setModalItems(data.result);
          setModalOpen(true);
          return;
        }

        await loadItemDetails(data.result[0]);
      } catch (error) {
        console.error("Error fetching:", error);
        log.error("search.error", { error: error?.message || String(error) });
        setNotFound(true);
        setGood(null);
        setStocks([]);
        setPrices([]);
        setSimilarItems([]);
      } finally {
        setLoading(false);
        setBarcode("");
        setManualSearch("");
        log.debug("search.finally");
      }
    },
    [apiKey, apiPost, isManualMode, loadItemDetails, log]
  );

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, [stopScanner]);

  useEffect(() => {
    if (isManualMode) return;

    const handleKey = (e) => {
      if (e.key === "Enter") {
        handleSearch(barcode);
      } else if (e.key.length === 1) {
        setBarcode((prev) => prev + e.key);
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [barcode, handleSearch, isManualMode]);

  const manualInputRef = useRef(null);

  const handleManualSearchSubmit = (e) => {
    e.preventDefault();
    handleSearch(manualSearch);
    manualInputRef.current?.blur();
  };

  return (
    <div className="App">
      {!apiKey && (
        <p style={{ color: "red" }}>
          Iltimos Integratsiya kalitingizni kiriting
        </p>
      )}

      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Найденные товары</h3>
            <ul>
              {modalItems.map((item, i) => (
                <li
                  key={i}
                  onClick={async () => {
                    setModalOpen(false);
                    await loadItemDetails(item);
                  }}
                  className="modal-item"
                >
                  {item.item.name} | {item.item.articul} | {item.price}
                </li>
              ))}
            </ul>
            <button className="close-btn" onClick={() => setModalOpen(false)}>
              Закрыть
            </button>
          </div>
        </div>
      )}

      <div className="search-mode-toggle" ref={toggleRef}>
        <label className="toggle-label">
          <span>Ручной поиск</span>
          <div className="toggle-switch">
            <input
              type="checkbox"
              checked={isManualMode}
              onChange={(e) => setIsManualMode(e.target.checked)}
            />
            <span className="slider"></span>
          </div>
        </label>
      </div>

      {isManualMode ? (
        <>
          <form
            onSubmit={handleManualSearchSubmit}
            className="manual-search-form"
          >
            <input
              ref={manualInputRef}
              type="text"
              placeholder="Введите название товара..."
              value={manualSearch}
              onChange={(e) => setManualSearch(e.target.value)}
              className="manual-search-input"
              autoFocus
            />
          </form>

          <button className="scan-btn" onClick={startScanner}>
            Сканировать штрихкод
          </button>

          {scanning && (
            <div className="scanner-container">
              <video
                ref={videoRef}
                playsInline
                style={{ width: "100%", maxWidth: "400px" }}
              />
              <button className="close-btn" onClick={stopScanner}>
                Отмена
              </button>
            </div>
          )}
        </>
      ) : (
        <input
          className="barcode-input"
          type="text"
          placeholder="Tovarning shtrix kodini skanerlang..."
          value={barcode}
          readOnly
          style={{ position: "absolute", left: "-9999px" }}
        />
      )}

      {loading && <p className="loading">Идёт поиск...</p>}

      {good ? (
        <>
          <div className="card">
            <div className="card-left">
              <h2>{good.name}</h2>
              <p>Артикул: {good.articul}</p>
              <p>Цвет: {good.color || ""}</p>
              <p>Размер: {good.size || ""}</p>
              <p>Кол-во: {good.quantity}</p>
              <p>Нархи: {good.price} so'm</p>
            </div>
            {good.image && (
              <div className="card-right">
                <img src={good.image || "/placeholder.svg"} alt={good.name} />
              </div>
            )}
          </div>

          {(stocks.length > 0 || prices.length > 0) && (
            <div className="card2" style={{ display: "flex", gap: "20px" }}>
              {stocks.length > 0 && (
                <div style={{ flex: 1 }}>
                  <table className="stock-table">
                    <thead>
                      <tr>
                        <th>Склад</th>
                        <th>Остаток</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stocks.map((s, i) => (
                        <tr key={i}>
                          <td>{s.stock?.name}</td>
                          <td>{s.common}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {prices.length > 0 && (
                <div style={{ flex: 1 }}>
                  <table className="price-table">
                    <thead>
                      <tr>
                        <th>Тип цены</th>
                        <th>Значение</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prices.map((p, i) => (
                        <tr key={i}>
                          <td>{p.price_type?.name}</td>
                          <td>{p.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {similarItems.length > 0 && (
            <div className="similar-card">
              <h3>Похожие товары</h3>
              <ul>
                {similarItems.map((s, i) => (
                  <li
                    key={i}
                    onClick={async () => {
                      await loadItemDetails(s);
                      toggleRef.current?.scrollIntoView({ behavior: "smooth" }); // ✅ scroll
                    }}
                  >
                    {s.item.name} | {s.item.color?.name} {s.item.size?.name} —{" "}
                    {s.price}
                    (остаток: {s.quantity?.common ?? 0})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : notFound ? (
        <p className="not-found">Товар не найден</p>
      ) : (
        !barcode &&
        !manualSearch && (
          <p className="main-message">
            {isManualMode
              ? "Введите название товара для поиска"
              : "Сканируйте штрихкод"}
          </p>
        )
      )}
    </div>
  );
}

export default App;
